import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { auth } from '../../services/firebase';
import { activationKey, creditNoticeStorageKey, isPaidUpgrade, safeStripeInvoiceUrl, type EconomicBillingStatus, type PendingPurchase, type PurchaseResidence } from './model';
import type { SubscriptionTier } from '../../config/subscriptionPlans';
import { useEconomicBillingCycleRenewalRefresh } from './useCycleRenewalRefresh';
import { readEconomicMeterCache, saveEconomicMeterCache, clearEconomicMeterCache, type EconomicMeterSnapshot } from './displayCache';

export interface EconomicBillingController {
  mode: 'loading' | 'legacy' | 'economic';
  status: EconomicBillingStatus | null;
  displayStatus?: EconomicMeterSnapshot | null;
  error: string | null;
  busy: boolean;
  recoveryRequired: boolean;
  showCreditsNotice: boolean;
  refresh: () => Promise<'legacy' | 'economic' | 'unavailable'>;
  activate: () => Promise<void>;
  checkout: (amountCents: 500 | 1000 | 2000) => Promise<void>;
  subscribe: (tier: SubscriptionTier) => Promise<void>;
  pendingPurchase: PendingPurchase | null;
  quotePurchase: (residence: PurchaseResidence) => Promise<void>;
  confirmPurchase: (acceptedTerms: boolean, requestImmediateStart: boolean) => Promise<void>;
  cancelPurchase: () => void;
  downloadContract: (requestId: string) => Promise<void>;
  acknowledgeMessage: (admittedStatus?: EconomicBillingStatus | null) => void;
  rejectAdmission: (unavailable: boolean) => void;
}
export const EconomicBillingContext = createContext<EconomicBillingController | null>(null);
export const useEconomicBilling = () => useContext(EconomicBillingContext);

async function request(path: string, body?: unknown) {
  const user = auth.currentUser;
  if (!user) throw new Error('Sesión no disponible.');
  const response = await fetch(`/api/billing/${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { Authorization: `Bearer ${await user.getIdToken()}`, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) })
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error === 'economic_billing_account_recovery_required'
      ? data.error : 'No se pudo actualizar tu plan y créditos.');
  }
  return response.json();
}

export function useEconomicBillingController(uid: string | null): EconomicBillingController {
  const [snapshot, setSnapshot] = useState<{ uid: string; status: EconomicBillingStatus | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [acknowledged, setAcknowledged] = useState<string | null>(null);
  const [admissionBlocked, setAdmissionBlocked] = useState(false);
  const [recoveryRequired, setRecoveryRequired] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<PendingPurchase | null>(null);
  const owner = useRef(uid);
  const presentation = useRef<{ uid: string | null; value: EconomicMeterSnapshot | null }>({ uid: null, value: null });
  if (presentation.current.uid !== uid) {
    if (presentation.current.uid) clearEconomicMeterCache(presentation.current.uid);
    presentation.current = { uid, value: uid ? readEconomicMeterCache(uid) : null };
  }
  owner.current = uid;
  const serverStatus = snapshot?.uid === uid ? snapshot.status : null;
  const mode = snapshot?.uid !== uid || !snapshot ? 'loading' : serverStatus ? 'economic' : 'legacy';
  const status = serverStatus && admissionBlocked && serverStatus.tier !== 'admin'
    ? { ...serverStatus, canStartExecution: false } : serverStatus;
  const refresh = useCallback(async () => {
    if (!uid) return 'unavailable' as const;
    try {
      const result = await request('status');
      if (owner.current !== uid) return 'unavailable' as const;
      if (result.mode !== 'legacy' && result.mode !== 'economic') throw new Error('Invalid billing status');
      setSnapshot({ uid, status: result.mode === 'economic' ? result : null });
      if (result.mode === 'economic') saveEconomicMeterCache(uid, result);
      else clearEconomicMeterCache(uid);
      presentation.current.value = result.mode === 'economic' ? readEconomicMeterCache(uid) : null;
      setAdmissionBlocked(false);
      setRecoveryRequired(false);
      setError(null);
      return result.mode as 'legacy' | 'economic';
    } catch (failure) {
      // A failed or pending read must never advertise the retired quota. Preserve a
      // previous server-selected view, scoped to this UID; admission still uses the server.
      if (owner.current === uid) {
        const recovery = failure instanceof Error && failure.message === 'economic_billing_account_recovery_required';
        if (recovery) setRecoveryRequired(true);
        if (recovery) { clearEconomicMeterCache(uid); presentation.current.value = null; }
        setError(recovery ? 'Tu cuenta está pendiente de verificación o conciliación de su saldo. El borrador se conserva.'
          : 'No se pudo actualizar tu plan y créditos.');
      }
      return 'unavailable' as const;
    }
  }, [uid]);
  useEffect(() => {
    setSnapshot(null); setError(null); setBusy(false); setAdmissionBlocked(false); setRecoveryRequired(false); setPendingPurchase(null);
    try { setAcknowledged(uid ? localStorage.getItem(creditNoticeStorageKey(uid)) : null); }
    catch { setAcknowledged(null); }
    void refresh();
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh, uid]);
  useEconomicBillingCycleRenewalRefresh(Boolean(status && status.tier !== 'admin'), status?.cycleEndAtMs ?? null, refresh);
  const activate = async () => {
    if (!status || busy) return;
    const actionOwner = uid;
    setBusy(true); setError(null);
    try {
      await request('credits/activate', { epochId: status.epochId, cycleReference: status.cycleReference });
      if (owner.current === actionOwner) await refresh();
    } catch { if (owner.current === actionOwner) setError('No se pudieron activar los créditos.'); }
    finally { if (owner.current === actionOwner) setBusy(false); }
  };
  const checkout = async (amountCents: 500 | 1000 | 2000) => {
    if (!status?.checkoutEnabled || status.fiscalState !== 'ready' || busy) return;
    setError(null); setPendingPurchase({ target: { kind: 'credits', amountCents }, requestId: crypto.randomUUID(), quote: null });
  };
  const subscribe = async (tier: SubscriptionTier) => {
    if (!status || busy || status.tier === 'admin' || tier === 'admin') return;
    if (isPaidUpgrade(status.tier, tier)) {
      if (!status.checkoutEnabled || status.fiscalState !== 'ready') return;
      setError(null); setPendingPurchase({ target: { kind: 'subscription', tier: tier as 'standard' | 'professional' | 'premium' }, requestId: crypto.randomUUID(), quote: null });
      return;
    }
    const actionOwner = uid;
    setBusy(true); setError(null);
    try {
      const result = await request('subscription', { tier, requestId: crypto.randomUUID() });
      if (owner.current !== actionOwner) return;
      if (typeof result.url === 'string' && new URL(result.url).protocol === 'https:') window.location.assign(result.url);
      else await refresh();
    } catch { if (owner.current === actionOwner) setError('No se pudo iniciar el cambio de plan.'); }
    finally { if (owner.current === actionOwner) setBusy(false); }
  };
  const quotePurchase = async (residence: PurchaseResidence) => {
    if (!pendingPurchase || busy) return;
    const actionOwner = uid, purchase = pendingPurchase;
    setBusy(true); setError(null);
    try {
      const quote = await request('quote', { requestId: purchase.requestId, target: purchase.target, residence });
      if (owner.current === actionOwner) setPendingPurchase(prior => prior?.requestId === purchase.requestId ? { ...prior, quote } : prior);
    } catch { if (owner.current === actionOwner) setError('No se pudo preparar el precio y las condiciones para tu residencia.'); }
    finally { if (owner.current === actionOwner) setBusy(false); }
  };
  const confirmPurchase = async (acceptedTerms: boolean, requestImmediateStart: boolean) => {
    const purchase = pendingPurchase;
    if (!purchase?.quote || busy || !acceptedTerms || !requestImmediateStart) return;
    if (purchase.quote.validUntilMs <= Date.now()) { setError('La oferta ha caducado. Revisa de nuevo el precio.'); return; }
    const actionOwner = uid;
    setBusy(true); setError(null);
    try {
      const acceptance = { quoteId: purchase.quote.quoteId, quoteDigest: purchase.quote.quoteDigest, acceptedTerms, requestImmediateStart };
      const result = await request(purchase.target.kind === 'credits' ? 'checkout' : 'subscription', {
        ...(purchase.target.kind === 'credits' ? { amountCents: purchase.target.amountCents } : { tier: purchase.target.tier }),
        requestId: purchase.requestId, acceptance });
      if (owner.current !== actionOwner) return;
      if (result.paymentRequired === true) {
        const paymentUrl = safeStripeInvoiceUrl(result.paymentUrl);
        if (paymentUrl) window.location.assign(paymentUrl);
        else {
          setPendingPurchase(null); await refresh();
          if (owner.current === actionOwner) setError('Completa el pago pendiente desde «Facturas y método de pago». Tu plan actual se conserva hasta confirmar el cobro.');
        }
        return;
      }
      if (typeof result.url === 'string' && new URL(result.url).protocol === 'https:') window.location.assign(result.url);
      else { setPendingPurchase(null); await refresh(); }
    } catch { if (owner.current === actionOwner) setError('No se pudo confirmar la contratación. Puedes volver a intentarlo.'); }
    finally { if (owner.current === actionOwner) setBusy(false); }
  };
  const downloadContract = async (requestId: string) => {
    try {
      const result = await request(`contract?requestId=${encodeURIComponent(requestId)}`);
      if (typeof result.text !== 'string' || owner.current !== uid) return;
      const url = URL.createObjectURL(new Blob([result.text], { type: 'text/plain;charset=utf-8' }));
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = `juris-contrato-${requestId}.txt`; anchor.click();
      URL.revokeObjectURL(url);
    } catch { setError('No se pudo descargar el documento de contratación.'); }
  };
  const cachedDisplay = mode === 'loading' && !recoveryRequired && presentation.current.uid === uid
    && presentation.current.value?.cycleEndAtMs && presentation.current.value.cycleEndAtMs > Date.now()
    ? presentation.current.value : null;
  return { mode, status, displayStatus: serverStatus ?? cachedDisplay, error, busy, recoveryRequired, refresh, activate, checkout, subscribe,
    pendingPurchase, quotePurchase, confirmPurchase, downloadContract, cancelPurchase: () => setPendingPurchase(null),
    showCreditsNotice: Boolean(status?.credits.activation === 'active' && !status.credits.noticeConsumed && status.credits.activationId && activationKey(status) !== acknowledged),
    rejectAdmission: (unavailable) => {
      if (owner.current !== uid) return;
      setAdmissionBlocked(true);
      setError(unavailable ? 'El servicio está temporalmente pausado. Tu borrador se conserva.' : 'Activa créditos o gestiona tu plan para continuar.');
      void refresh();
    },
    acknowledgeMessage: (admittedStatus = status) => {
      if (owner.current !== uid) return;
      if (uid && admittedStatus?.credits.activation === 'active' && admittedStatus.credits.activationId) {
        const key = activationKey(admittedStatus);
        setAcknowledged(key);
        try { localStorage.setItem(creditNoticeStorageKey(uid), key); } catch { /* In-memory acknowledgement remains available. */ }
      }
      void refresh();
    }
  };
}
